import React from 'react';
import { useState } from 'react';
import { Alert, Card, CardContent, TextField, MenuItem, Select } from '@mui/material';
import * as yup from 'yup';
import { useDispatch } from 'react-redux';
import { showNotification } from '../../../redux/slices/notificationSlice';
import CustomButton from '../../../componentsII/Buttons';
import api from '../../../axios/api';

// Define validation schema
const schema = yup.object().shape({
  typeValue: yup.string().required('Type Value is required'),
  typeCode: yup.string().required('Type Code is required'),
  typeLabel: yup.string().required('Type Label is required'),
  unitCode: yup.string()
    .matches(/^[A-Z]+$/, 'Unit Code must be uppercase')
    .required('Unit Code is required'),
});

const AddUpdateGeneralModal = ({ refetch, onClose, GeneralId = null, initialData = null }) => {
  const dispatch = useDispatch();

  // Form states
  const [formData, setFormData] = useState({
    typeValue: initialData?.type_value || '',
    typeLabel: initialData?.type_label || '',
    typeCode: initialData?.type_code || '',
    unitCode:initialData?.unit_code || '',
  });


  // UI states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [generalError, setGeneralError] = useState('');

  // const handleInputChange = (field) => (e) => {
  //   setFormData(prev => ({
  //     ...prev,
  //     [field]: e.target.value
  //   }));
  //   // Clear error for this field
  //   setErrors(prev => ({
  //     ...prev,
  //     [field]: ''
  //   }));
  //   setGeneralError('');
  // };

  const handleInputChange = (field) => (e) => {
    let value = e.target.value;
    if (field === 'unitCode') {
      value = value.toUpperCase();
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  
    setErrors(prev => ({
      ...prev,
      [field]: ''
    }));
    
    setGeneralError('');
  };
  

  const validateForm = async () => {
    try {
      await schema.validate(formData, { abortEarly: false });
      return true;
    } catch (err) {
      const validationErrors = {};
      err.inner.forEach((error) => {
        validationErrors[error.path] = error.message;
      });
      setErrors(validationErrors);
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGeneralError('');
    setErrors({});

    try {
      const isValid = await validateForm();
      if (!isValid) return;

      setIsSubmitting(true);

      const payload = {
        GeneralId: GeneralId || 0,
        typeCode: formData.typeCode,
        typeValue: formData.typeValue.trim(),
        typeLabel: formData.typeLabel.trim(),
        unitCode: formData.unitCode.trim().toUpperCase(),
      };

      const response = await api.post('/admin/CreateUpdateGeneral', payload);

      if (response.data.status === '200') {
        dispatch(showNotification({
          message: response.data.message || 'Operation successful',
          type: "success"
        }));
        onClose();
        refetch();
      } else {
        throw new Error(response.data.message || 'Operation failed');
      }
    } catch (err) {
      setGeneralError(err.message || 'An error occurred while saving');
      dispatch(showNotification({
        message: err.message || 'An error occurred',
        type: "error"
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const typeCodeOptions = [
    { value: 'AREA', label: 'AREA' },
    { value: 'VOLUME', label: 'VOLUME' },
    { value: 'LENGTH', label: 'LENGTH' },
    { value: 'MASS', label: 'MASS' },
    { value: 'LIQUID', label: 'LIQUID' },
    { value: 'ELECTRICITY', label: 'ELECTRICITY' },
    { value: 'HEAT', label: 'HEAT' },
    { value: 'STEEM', label: 'STEEM' },
    { value: 'COOLING', label: 'COOLING' },
  ];

  return (
    <Card className="w-full mx-auto">
      <CardContent>
        {generalError && (
          <Alert severity="error" className="mb-4">
            {generalError}
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="typeCode" className="text-sm font-medium">
              Type Code
            </label>
            <Select
              id="typeCode"
              value={formData.typeCode}
              onChange={handleInputChange('typeCode')}
              placeholder="Select Type Code"
              disabled={isSubmitting}
              size="small"
              error={Boolean(errors.typeCode)}
              className="w-full"
            >
              {typeCodeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {errors.typeCode && (
              <p className="text-sm text-red-500 mt-1">{errors.typeCode}</p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="typeValue" className="text-sm font-medium">
              Type Value
            </label>
            <TextField
              id="typeValue"
              value={formData.typeValue}
              onChange={handleInputChange('typeValue')}
              placeholder="Enter Type Value"
              disabled={isSubmitting}
              size="small"
              error={Boolean(errors.typeValue)}
              className="w-full"
            />
            {errors.typeValue && (
              <p className="text-sm text-red-500 mt-1">{errors.typeValue}</p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="typeLabel" className="text-sm font-medium">
              Type Label
            </label>
            <TextField
              id="typeLabel"
              value={formData.typeLabel}
              onChange={handleInputChange('typeLabel')}
              placeholder="Enter Type Label"
              disabled={isSubmitting}
              size="small"
              error={Boolean(errors.typeLabel)}
              className="w-full"
            />
            {errors.typeLabel && (
              <p className="text-sm text-red-500 mt-1">{errors.typeLabel}</p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="unitCode" className="text-sm font-medium">
              Unit Code
            </label>
            <TextField
              id="unitCode"
              value={formData.unitCode}
              onChange={handleInputChange('unitCode')}
              placeholder="Enter Unit Code"
              disabled={isSubmitting}
              size="small"
              error={Boolean(errors.unitCode)}
              className="w-full"
            />
            {errors.unitCode && (
              <p className="text-sm text-red-500 mt-1">{errors.unitCode}</p>
            )}
          </div>

          <div className="flex justify-end gap-5 items-center mt-5">
            <CustomButton
              className="bg-gray-500 hover:bg-gray-700 text-white"
              text="Cancel"
              click={onClose}
              disabled={isSubmitting}
            />
            <CustomButton
              className="bg-green-600 hover:bg-green-700 text-white"
              text={GeneralId ? "Update" : "Submit"}
              type="submit"
              disabled={isSubmitting}
            />
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default AddUpdateGeneralModal;